import React, { cloneElement, useEffect, useRef, useState } from 'react';

import { useMRE } from '../../hooks/useMRE';
import { LatLng } from '../../types';
import { PlaceHTMLOnMap } from '../PlaceHTMLOnMap/PlaceHTMLOnMap';
import { DraggableHTMLWaypoint } from './DraggableHTMLWaypoint/DraggableHTMLWaypoint';

import styles from './DraggableHTML.module.scss';
interface DraggableProps {
  children: React.ReactElement;
  latLng: LatLng;
  onPositionUpdated: (latLng: LatLng) => void;
  onDrag?: () => void;
  onClick?: () => void;
}
interface DraggableComponent extends React.FC<DraggableProps> {
  Waypoint: typeof DraggableHTMLWaypoint;
}

const DRAG_THRESHOLD_PIXELS = 5; // below this threshold, we may treat as click
// When the user clicks on a waypoint they may accidentally move it a bit, causing an
// annoying route re-calculation. We try to filter such circumstances out with the below.
const isDragBigEnoughToRegister = (
  current: { x: number; y: number },
  initial: { x: number; y: number }
): boolean => {
  const dragDistanceX = current.x - initial.x;
  const dragDistanceY = current.y - initial.y;
  const totalDragDistance = Math.sqrt(
    dragDistanceX * dragDistanceX + dragDistanceY * dragDistanceY
  );
  return totalDragDistance >= DRAG_THRESHOLD_PIXELS;
};

export const DraggableHTML: DraggableComponent = ({
  children,
  onDrag,
  onClick,
  onPositionUpdated,
  latLng
}: DraggableProps) => {
  // Destructure latLng array into primitives for useEffect dependencies.
  // This prevents effect re-runs when the array reference changes but the lat/lng values remain the same.
  const [lat, lng] = latLng ?? [];

  // draggingLatLng will change very frequently so some optimisation is warranted.
  // We want to be able to use draggingLatLng to control the position of the rendered component (clean
  // with state so update trigger a rerender), but we also be able to read the value in event handlers,
  // without needing to rebind the handlers each time the value changes. Therefore we use both state
  // and a ref to track the value.
  const [draggingLatLng, _setDraggingLatLng] = useState<LatLng>(latLng);
  const draggingLatLngRef = useRef(draggingLatLng);
  const setDraggingLatLng = (latLng: LatLng) => {
    draggingLatLngRef.current = latLng;
    _setDraggingLatLng(latLng);
  };

  // When the underlying element location is updated, immediately set the dragging location to match.
  // This may result in the mouse no longer being over the rendered element, until the next move.
  // However, this is arguably quite a nice UX.
  useEffect(() => {
    setDraggingLatLng([lat, lng]);
  }, [lat, lng]);

  // This will be used to track if a drag is so small that it should in fact be treated as a click.
  const pointerDownPos = useRef({ x: 0, y: 0 });

  const [isDragging, setIsDragging] = useState(false);

  const { terrainEngine, mapContainerRef } = useMRE();

  // We will implement a debounced onPositionUpdate call during dragging (i.e.
  // when mouse has been stationary for some time). This replicates existing
  // behaviour in the legacy routebuilder; whereby if the mouse is held stationary,
  // the drag remains in progress, but the route will be updated.
  const timeoutHandle = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // timeout will be cleared on drag end (pointer up), but we should also clear
    // it up if the component unmounts during a drag.
    return () => {
      clearTimeout(timeoutHandle.current);
    };
  }, []);

  const draggableElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const draggableElement = draggableElementRef.current;

    if (!draggableElement) {
      return;
    }

    const onPointerDown = (evt: PointerEvent) => {
      draggableElement.setPointerCapture(evt.pointerId);
      pointerDownPos.current = { x: evt.clientX, y: evt.clientY };
      setIsDragging(true);
    };

    const onPointerMove = (evt: PointerEvent) => {
      if (
        !draggableElement.hasPointerCapture(evt.pointerId) ||
        !mapContainerRef.current ||
        !terrainEngine
      ) {
        return;
      }

      const { offsetLeft, offsetTop, offsetWidth, offsetHeight } =
        mapContainerRef.current;

      const point = terrainEngine
        .getCamera()
        .getCustomLookAtPoint(
          (evt.clientX - offsetLeft) / offsetWidth,
          (evt.clientY - offsetTop) / offsetHeight
        );

      setDraggingLatLng([
        point.worldPoint.latitude,
        point.worldPoint.longitude
      ]);

      clearTimeout(timeoutHandle.current);

      if (
        isDragBigEnoughToRegister(
          { x: evt.clientX, y: evt.clientY },
          pointerDownPos.current
        )
      ) {
        timeoutHandle.current = setTimeout(() => {
          onPositionUpdated(draggingLatLngRef.current);
        }, 300);
      }

      onDrag?.();
    };

    const onPointerUp = (evt: PointerEvent) => {
      draggableElement.releasePointerCapture(evt.pointerId);
      setIsDragging(false);
      clearTimeout(timeoutHandle.current);

      if (
        isDragBigEnoughToRegister(
          { x: evt.clientX, y: evt.clientY },
          pointerDownPos.current
        )
      ) {
        onPositionUpdated(draggingLatLngRef.current);
      } else {
        // When we finish the drag as a click, it is important to set the dragging location back to the
        // original location. If we don't do this, we can end up with the draggable HTML starting off in
        // a slightly misaligned location for the next interaction!
        setDraggingLatLng([lat, lng]);
        onClick?.();
      }
    };

    const onContextMenu = (evt: MouseEvent) => {
      // Prevent default browser context menu; we never want to show this for waypoint clicks.
      // Instead, we can show our own menu via the parent component responding to the onClick callback prop.
      evt.preventDefault();
    };

    draggableElement.addEventListener('pointerdown', onPointerDown);
    draggableElement.addEventListener('pointermove', onPointerMove);
    draggableElement.addEventListener('pointerup', onPointerUp);
    draggableElement.addEventListener('contextmenu', onContextMenu);

    return () => {
      draggableElement.removeEventListener('pointerdown', onPointerDown);
      draggableElement.removeEventListener('pointermove', onPointerMove);
      draggableElement.removeEventListener('pointerup', onPointerUp);
      draggableElement.removeEventListener('contextmenu', onContextMenu);
    };
  }, [
    onDrag,
    onClick,
    onPositionUpdated,
    lat,
    lng,
    terrainEngine,
    mapContainerRef
  ]);

  return (
    <PlaceHTMLOnMap latLng={draggingLatLng} allowPointerEvents>
      <div className={styles.draggable} ref={draggableElementRef}>
        {cloneElement(children, { isDragging })}
      </div>
    </PlaceHTMLOnMap>
  );
};

DraggableHTML.Waypoint = DraggableHTMLWaypoint;
